/**
 * Address masking utilities for security
 * 
 * Før booking: Vis kun by/postcode (e.g., "København N" eller "2200 København N")
 * Efter booking: Vis fuld adresse
 */

/**
 * Parse dansk adresse til by/postcode
 * Format: "Vejnavn 42, 2200 København N" -> "2200 København N"
 */
export function maskAddress(fullAddress: string): string {
  // Hvis der er et komma, tag alt efter kommaet
  if (fullAddress.includes(',')) {
    const parts = fullAddress.split(',');
    return parts[1]?.trim() || fullAddress;
  }
  
  // Hvis der er 4 cifre (postcode), tag fra postcode og fremefter
  const postcodeMatch = fullAddress.match(/\b\d{4}\b/);
  if (postcodeMatch && postcodeMatch.index !== undefined) {
    return fullAddress.substring(postcodeMatch.index).trim();
  }
  
  // Fallback: returner hele adressen hvis format er ukendt
  return fullAddress;
}

/**
 * Få område/by fra adresse (uden postnummer hvis muligt)
 * "Nørrebrogade 42, 2200 København N" -> "København N"
 * "Copenhagen, Denmark" -> "Copenhagen"
 */
export function getAreaFromAddress(address: string): string {
  const masked = maskAddress(address);
  
  // Hvis der er postcode (4 cifre), fjern det
  const withoutPostcode = masked.replace(/^\d{4}\s*/, '');
  
  // Hvis det er et internationalt format (f.eks. "Copenhagen, Denmark"), tag første del
  if (withoutPostcode.includes(',')) {
    return withoutPostcode.split(',')[0].trim();
  }
  
  return withoutPostcode || masked;
}

/**
 * Mask address for map display - shows street name (without number) + postcode/city
 * "Thit Jensens Gade 42, 8000 Aarhus C" -> "Thit Jensens Gade, 8000 Aarhus C"
 * "Nørrebrogade 42, 2200 København N" -> "Nørrebrogade, 2200 København N"
 */
export function maskAddressForMap(fullAddress: string): string {
  if (!fullAddress.includes(',')) {
    return maskAddress(fullAddress);
  }
  
  const parts = fullAddress.split(',');
  const streetPart = parts[0]?.trim() || '';
  const restPart = parts.slice(1).join(',').trim();
  
  // Remove house number from street name (assumes number is at the end)
  // "Thit Jensens Gade 42" -> "Thit Jensens Gade"
  const streetWithoutNumber = streetPart.replace(/\s+\d+[a-zA-Z]?(\s+\d+)?$/, '');
  
  return `${streetWithoutNumber}, ${restPart}`;
}
